<script setup lang="ts">
  import { onMounted } from 'vue';
  import PrinterIcon from '@/shared/assets/icons/PrinterIcon.svg';
  import { IconImage, SimpleLoading } from '@/shared/ui';
  import PermissionsListHeader from './PermissionsListHeader.vue';
  import { usePermissionsListPage } from './usePermissionsListPage';

  const {
    loading,
    error,
    reportDate,
    registrationReport,
    positionReport,
    functionalActivityReport,
    fetchReport,
    handlePrint,
  } = usePermissionsListPage();

  onMounted(() => {
    fetchReport();
  });
</script>

<template>
  <div
    id="permissions-report-content"
    data-print="root"
    class="bg-white-01 p-6 text-gray-07 [font-family:Outfit,sans-serif] max-md:p-4 print:p-0"
  >
    <PermissionsListHeader :report-date="reportDate" />

    <button
      data-print="button"
      class="ml-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded border-0 bg-[var(--theme-secondary)] text-white transition-opacity hover:opacity-90 print:hidden"
      @click="handlePrint"
    >
      <IconImage
        name="PrinterIcon"
        :src="PrinterIcon"
        alt="Imprimir"
      />
    </button>

    <div
      data-print="divider"
      class="my-6 border-t border-gray-02"
    />

    <div
      v-if="loading"
      class="w-full flex justify-center"
    >
      <SimpleLoading />
    </div>

    <div
      v-else-if="error"
      class="text-center py-8"
    >
      <div class="text-red-500 text-lg">
        {{ error }}
      </div>
      <button
        class="mt-4 rounded bg-[var(--theme-secondary)] px-4 py-2 text-white hover:opacity-90 print:hidden"
        @click="fetchReport"
      >
        Tentar novamente
      </button>
    </div>

    <div
      v-else-if="registrationReport"
      class="flex flex-col gap-5"
    >
      <div
        data-print="panel"
        class="overflow-hidden rounded-2xl border border-gray-02 bg-white-01"
      >
        <div class="overflow-x-auto">
          <table
            data-print="data-table"
            class="w-full border-collapse"
          >
            <thead>
              <tr class="border-b border-gray-02">
                <th
                  class="px-[14px] py-[10px] text-left text-sm leading-[18px] font-semibold whitespace-nowrap"
                >
                  Nome
                </th>
                <th
                  class="px-[14px] py-[10px] text-left text-sm leading-[18px] font-semibold whitespace-nowrap"
                >
                  Matrícula
                </th>
                <th
                  class="px-[14px] py-[10px] text-left text-sm leading-[18px] font-semibold whitespace-nowrap"
                >
                  Departamento
                </th>
                <th
                  class="px-[14px] py-[10px] text-left text-sm leading-[18px] font-semibold whitespace-nowrap"
                >
                  Cargo
                </th>
                <th
                  class="px-[14px] py-[10px] text-left text-sm leading-[18px] font-semibold whitespace-nowrap"
                >
                  Atividades Funcionais
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-t border-gray-08">
                <td class="px-[14px] py-[10px] text-left text-sm leading-[18px]">
                  {{ registrationReport.user.name }}
                </td>
                <td class="px-[14px] py-[10px] text-left text-sm leading-[18px]">
                  {{ registrationReport.user.registrationNumber ?? '-' }}
                </td>
                <td class="px-[14px] py-[10px] text-left text-sm leading-[18px]">
                  {{ registrationReport.user.department ?? registrationReport.department ?? '-' }}
                </td>
                <td class="px-[14px] py-[10px] text-left text-sm leading-[18px]">
                  {{ registrationReport.position ?? '-' }}
                </td>
                <td class="px-[14px] py-[10px] text-left text-sm leading-[18px]">
                  {{
                    registrationReport.functionalActivities.length > 0
                      ? registrationReport.functionalActivities.map((item) => item.name).join(', ')
                      : '-'
                  }}
                </td>
              </tr>
              <tr class="border-t border-gray-08">
                <td
                  colspan="5"
                  class="px-[14px] pt-5 pb-2 text-left text-sm leading-5 font-semibold"
                >
                  Permissões
                </td>
              </tr>
              <tr>
                <td
                  colspan="5"
                  class="px-[14px] pt-3 pb-[14px]"
                >
                  <div
                    data-print="permissions-grid"
                    class="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-x-4 gap-y-4 max-md:grid-cols-[repeat(auto-fit,minmax(220px,1fr))]"
                  >
                    <article
                      v-for="category in registrationReport.permissionsByCategory"
                      :key="`${category.id ?? 'none'}-${category.name}`"
                      data-print="category"
                      class="flex min-w-0 flex-col gap-2"
                    >
                      <h3 class="text-sm leading-[18px] font-semibold">
                        {{ category.name }}
                      </h3>
                      <div
                        data-print="category-items"
                        class="flex flex-col items-start gap-[6px]"
                      >
                        <span
                          v-for="permission in category.permissions"
                          :key="permission.id"
                          class="block break-words text-sm leading-4"
                        >
                          {{ permission.description?.trim() || permission.name }}
                        </span>
                      </div>
                    </article>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div
      v-else-if="positionReport"
      class="flex flex-col gap-5"
    >
      <div
        v-for="position in positionReport.positions"
        :key="position.id"
        data-print="panel"
        class="overflow-hidden rounded-2xl border border-gray-02 bg-white-01"
      >
        <div class="overflow-x-auto">
          <table
            data-print="data-table"
            class="w-full border-collapse"
          >
            <thead>
              <tr>
                <th
                  class="px-[14px] py-[10px] text-left text-sm leading-[18px] font-semibold whitespace-nowrap"
                >
                  Cargo
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="px-[14px] py-[10px] text-left text-sm leading-[18px]">
                  {{ position.name || '-' }}
                </td>
              </tr>
              <tr class="border-t border-gray-08">
                <td class="px-[14px] pt-5 pb-2 text-left text-sm leading-5 font-semibold">
                  Permissões
                </td>
              </tr>
              <tr>
                <td class="px-[14px] pt-3 pb-[14px]">
                  <div
                    data-print="permissions-grid"
                    class="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-x-4 gap-y-4 max-md:grid-cols-[repeat(auto-fit,minmax(220px,1fr))]"
                  >
                    <article
                      v-for="category in position.permissionsByCategory"
                      :key="`${category.id ?? 'none'}-${category.name}`"
                      data-print="category"
                      class="flex min-w-0 flex-col gap-2"
                    >
                      <h3 class="text-sm leading-[18px] font-semibold">
                        {{ category.name }}
                      </h3>
                      <div
                        data-print="category-items"
                        class="flex flex-col items-start gap-[6px]"
                      >
                        <span
                          v-for="permission in category.permissions"
                          :key="permission.id"
                          class="block break-words text-sm leading-4"
                        >
                          {{ permission.description?.trim() || permission.name }}
                        </span>
                      </div>
                    </article>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div
      v-else-if="functionalActivityReport"
      class="flex flex-col gap-5"
    >
      <div
        v-for="functionalActivity in functionalActivityReport.functionalActivities"
        :key="functionalActivity.id"
        data-print="panel"
        class="overflow-hidden rounded-2xl border border-gray-02 bg-white-01"
      >
        <div class="overflow-x-auto">
          <table
            data-print="data-table"
            class="w-full border-collapse"
          >
            <thead>
              <tr>
                <th
                  class="px-[14px] py-[10px] text-left text-sm leading-[18px] font-semibold whitespace-nowrap"
                >
                  Atividade Funcional
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="px-[14px] py-[10px] text-left text-sm leading-[18px]">
                  {{ functionalActivity.name || '-' }}
                </td>
              </tr>
              <tr class="border-t border-gray-08">
                <td class="px-[14px] pt-5 pb-2 text-left text-sm leading-5 font-semibold">
                  Permissões
                </td>
              </tr>
              <tr>
                <td class="px-[14px] pt-3 pb-[14px]">
                  <div
                    data-print="permissions-grid"
                    class="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-x-4 gap-y-4 max-md:grid-cols-[repeat(auto-fit,minmax(220px,1fr))]"
                  >
                    <article
                      v-for="category in functionalActivity.permissionsByCategory"
                      :key="`${category.id ?? 'none'}-${category.name}`"
                      data-print="category"
                      class="flex min-w-0 flex-col gap-2"
                    >
                      <h3 class="text-sm leading-[18px] font-semibold">
                        {{ category.name }}
                      </h3>
                      <div
                        data-print="category-items"
                        class="flex flex-col items-start gap-[6px]"
                      >
                        <span
                          v-for="permission in category.permissions"
                          :key="permission.id"
                          class="block break-words text-sm leading-4"
                        >
                          {{ permission.description?.trim() || permission.name }}
                        </span>
                      </div>
                    </article>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
